import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Service } from 'src/app/models/centrex/service';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-service-form',
    templateUrl: './service-form.component.html',
    styleUrls: ['./service-form.component.scss'],
    imports: [FaIconComponent, ReactiveFormsModule, MatCard, MatCardContent, MatFormField, MatLabel, MatInput, MatButton, RouterLink, TranslateModule]
})
export class ServiceFormComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    serviceInformations: Service;
    serviceForm: UntypedFormGroup;
    isCreation: boolean;

    private previousInstance: Instance;

    constructor(
        private route: ActivatedRoute,
        private serviceService: ServiceService,
        private organizationsService: OrganizationsService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.createForm({} as Service);

        this.previousInstance = this.instanceService.instance();

        effect(() => {
            if (this.instanceService.instance() && this.previousInstance.id !== this.instanceService.instance().id) {
                this.router.navigate(['/services']);
            }
        });
    }

    ngOnInit(): void {
        let serviceName: string;

        // Récupère l'identifiant du service
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            serviceName = params['serviceName'];
        });

        this.loadDatas(serviceName);
    }

    loadDatas(serviceName: string) {
        this.spinner.show();

        if (serviceName) {
            this.serviceService
                .getService(serviceName)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (service) => {
                        this.serviceInformations = service;
                        this.isCreation = false;

                        // Créé le formulaire
                        this.createForm(this.serviceInformations);
                    },
                    error: () => {
                        this.spinner.hide();
                    },
                });
        } else {
            this.isCreation = true;
            this.serviceInformations = new Service();
            this.serviceInformations.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';

            // Créé le formulaire
            this.createForm(this.serviceInformations);
        }
    }

    createForm(service: Service) {
        this.serviceForm = this.formbuilder.group({
            organizationId: [service.organizationId],
            name: [service.name],
            settings: [service.settings],
        });

        this.spinner.hide();
    }
    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.serviceService
                .addService(this.serviceInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Service.SuccessMessageCreateService'));

                        // Redirige sur la page des services
                        this.router.navigate(['/services']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Service.ErrorMessageCreateService'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.serviceService
                .updateService(this.serviceInformations)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.toastr.success(this.translate.instant('Service.SuccessMessageUpdateService'));

                        this.router.navigate(['/services']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Service.ErrorMessageUpdateService'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.serviceInformations.organizationId = this.serviceForm.value.organizationId;
        this.serviceInformations.name = this.serviceForm.value.name;
        this.serviceInformations.settings = this.serviceForm.value.settings;
    }
}
